<template>
  <b-modal v-model="visible" :title="title" size="lg" @hidden="$emit('close')">
    <div class="mb-3">
      <icon-button
        :func="() => downloadTemplate()"
        :icon="'download'"
        :title="'Download CSV template'"
        :classes="'akkurate-gray-btn btn-size2'"
        :btnName="'Download template'"
        :disabled="downloading"
      />
    </div>
    <b-form-group label="CSV file">
      <b-form-file
        v-model="file"
        accept=".csv,text/csv"
        placeholder="Choose a CSV file or drop it here..."
        class="akkurate_input filter-list filter-list-choice status-filter custom-select"
        drop-placeholder="Drop CSV here..."
        @input="onFileSelected"
      />
    </b-form-group>
    <div class="options">
      <b-form-checkbox
        v-if="showUpdateExisting"
        v-model="updateExisting"
        class="mb-2"
      >
        Update existing records when matched
      </b-form-checkbox>
      <b-form-checkbox
        v-if="showOverwriteQty"
        v-model="overwriteQty"
        class="mb-2"
      >
        Overwrite Quatity
      </b-form-checkbox>
    </div>
    <div v-if="previewRows.length" class="mt-3">
      <p class="small text-muted mb-1">
        Preview (first {{ previewRows.length }} rows)
      </p>
      <div class="table-responsive bulk-preview-table">
        <table class="table table-sm table-bordered mb-0">
          <thead>
            <tr>
              <th v-for="col in previewHeaders" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in previewRows" :key="i">
              <td v-for="col in previewHeaders" :key="col">{{ row[col] }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="result" class="mt-3">
      <b-alert show :variant="result.errors?.length ? 'warning' : 'success'">
        <span v-if="result.created !== undefined"
          >Created: {{ result.created }}.
        </span>
        <span v-if="result.updated !== undefined"
          >Updated: {{ result.updated }}.
        </span>
        <span v-if="result.adjusted !== undefined"
          >Adjusted: {{ result.adjusted }}.
        </span>
        <span v-if="result.skipped !== undefined"
          >Skipped: {{ result.skipped }}.</span
        >
      </b-alert>
      <ul v-if="normalizedErrors.length" class="small text-danger mb-0">
        <li v-for="(err, i) in normalizedErrors.slice(0, 10)" :key="i">
          Row {{ err.row }}: {{ err.message }}
        </li>
        <li v-if="normalizedErrors.length > 10">
          …and {{ normalizedErrors.length - 10 }} more
        </li>
      </ul>
    </div>
    <template #modal-footer>
      <icon-button
        :func="() => (visible = false)"
        :icon="'times'"
        :title="'Close'"
        :classes="'akkurate-danger-bg btn-size2'"
        :btnName="'Close'"
      />
      <icon-button
        :func="() => submit()"
        :icon="'upload'"
        :title="'Import'"
        :classes="'akkurate-green-btn btn-size2'"
        :btnName="importing ? 'Importing…' : 'Import'"
        :disabled="!csvContent || importing"
      />
    </template>
  </b-modal>
</template>

<script>
import Vue from "vue";
import Swal from "sweetalert2";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";

// NOTE: if your project already defines a shared `toast` helper (e.g. in
// @/core/services or a mixin), delete this and import that one instead so
// styling/behavior stays consistent across the app.
const toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

/**
 * Parses a single CSV line into an array of field values, respecting
 * double-quoted fields (including escaped "" quotes and embedded commas).
 * This is a small state machine rather than a regex so it doesn't produce
 * spurious trailing empty matches on quoted/trailing-comma lines.
 */
function parseCsvLine(line) {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          // Escaped quote inside a quoted field
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
}

function parseCsv(text) {
  const lines = text
    .replace(/^\uFEFF/, "")
    .trim()
    .split(/\r?\n/);
  if (!lines.length) return { headers: [], rows: [] };

  const headers = parseCsvLine(lines[0]);
  const rows = lines
    .slice(1)
    .filter((l) => l.trim())
    .map((line) => {
      const values = parseCsvLine(line);
      const obj = {};
      headers.forEach((h, i) => {
        obj[h] = values[i] !== undefined ? values[i] : "";
      });
      return obj;
    });
  return { headers, rows };
}

export default {
  name: "BulkImportModal",
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, required: true },
    templateEndpoint: { type: String, required: true },
    importEndpoint: { type: String, required: true },
    storeId: { type: String, default: null },
    showUpdateExisting: { type: Boolean, default: true },
    showOverwriteQty: { type: Boolean, default: false },
  },
  components: {
    IconButton,
  },
  data() {
    return {
      file: null,
      csvContent: "",
      previewHeaders: [],
      previewRows: [],
      updateExisting: true,
      overwriteQty: false,
      importing: false,
      downloading: false,
      result: null,
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    // Defensively normalizes backend error entries so a differently-shaped
    // error object (missing row/message, or a plain string) never renders
    // "undefined" in the UI.
    normalizedErrors() {
      const errors = this.result?.errors;
      if (!Array.isArray(errors)) return [];
      return errors.map((err, i) => {
        if (err && typeof err === "object") {
          return {
            row: err.row ?? "?",
            message: err.message || "Unknown error",
          };
        }
        return { row: "?", message: String(err ?? `Error ${i + 1}`) };
      });
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.file = null;
        this.csvContent = "";
        this.previewHeaders = [];
        this.previewRows = [];
        this.result = null;
      }
    },
  },
  methods: {
    onFileSelected(file) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.csvContent = e.target.result;
        const parsed = parseCsv(this.csvContent);
        this.previewHeaders = parsed.headers;
        this.previewRows = parsed.rows.slice(0, 5);
      };
      reader.onerror = () => {
        toast.fire({
          title: "Could not read the selected file",
          icon: "error",
        });
      };
      reader.readAsText(file);
    },
    async downloadTemplate() {
      this.downloading = true;
      try {
        const response = await Vue.axios.get(this.templateEndpoint, {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = "template.csv";
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not download template",
          icon: "error",
        });
      } finally {
        this.downloading = false;
      }
    },
    async submit() {
      if (!this.csvContent) return;
      this.importing = true;
      this.result = null;
      try {
        const body = {
          csv: this.csvContent,
          updateExisting: this.updateExisting,
          overwriteQty: this.overwriteQty,
        };
        if (this.storeId) body.storeId = this.storeId;
        this.result = await ApiService.post(this.importEndpoint, body);
        this.$emit("imported", this.result);
        toast.fire({
          title: "Import completed",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Import failed",
          icon: "error",
        });
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.bulk-preview-table {
  max-height: 200px;
  overflow: auto;
}

.options {
  display: flex;
  gap: 10px;
}
</style>
